import { useTranslations } from 'next-intl';
import { Form, TextInput } from '@mentimeter/ragnar-form';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useMemo } from 'react';

export const ConfirmWithConsent = ({
  subscription,
  onDismiss,
  onJoinWorkspaceClick,
  isLoading,
}: {
  subscription: string;
  onDismiss: () => void;
  onJoinWorkspaceClick: () => void;
  isLoading: boolean;
}) => {
  const t = useTranslations('onboarding.join_workspace');
  const confirmationMessage = t('confirm_cancel_input_message');
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        'input-consent': Yup.string()
          .required(
            t('confirm_cancel_input_required_message', { confirmationMessage }),
          )
          .oneOf(
            [confirmationMessage],
            t('confirm_cancel_input_one_of_message', { confirmationMessage }),
          ),
      }),
    [t, confirmationMessage],
  );

  const form = useFormik({
    initialValues: { 'input-consent': '' },
    enableReinitialize: true,
    validationSchema,
    onSubmit: onJoinWorkspaceClick,
  });

  return (
    <>
      <Box flexDirection="column" width="100%">
        <Text color="textNegative" mb={1}>
          {t('cancel_subscription_message', { subscription })}
        </Text>
        <Text>{t('type_cancel_confirmation_message')}</Text>
      </Box>
      <Form
        validationSchema={validationSchema}
        initialValues={form.initialValues}
        onSubmit={form.handleSubmit}
        width="100%"
      >
        <>
          <TextInput
            label=""
            name="input-consent"
            id="input-consent"
            data-testid="input-field-consent-cancel-subscription"
            onChange={form.handleChange}
            width={['100%', '80%']}
            mt={1}
          />
          <Box
            flexDirection={['column', 'row']}
            width="100%"
            justifyContent="flex-end"
            mt={2}
          >
            <Button
              variant="secondary"
              mr={1}
              onClick={onDismiss}
              width={['100%', 'auto', 'auto']}
            >
              {t('keep_subscription_button')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={
                !form.isValid ||
                form.values['input-consent'].length < 1 ||
                isLoading
              }
              width={['100%', 'auto', 'auto']}
              mt={[2, 0]}
            >
              {t('join_workspace_button')}
            </Button>
          </Box>
        </>
      </Form>
    </>
  );
};
